/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {
  CSSProperties,
  useCallback,
  useMemo
} from 'react';
import {Grid, Theme} from '@material-ui/core';
import Button, {ButtonProps} from '@material-ui/core/Button';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {dlsColor, dlsShadow} from '@ion/dls/src/theme/theme';
import {OMIT_CLICK_AWAY_LISTENER} from '../../../maps/constants';
import {
  DLSShadowStyle,
  IconButtonDirectionEnum
} from '../../../types/types';
import {StableButtonLabel} from './StableButtonLabel';

interface TertiaryIconButtonProps extends Omit<ButtonProps, 'onClick' | 'id'> {
  id?: number;
  fill?: boolean;
  icon?: React.ReactElement<{color?: string}>;
  label?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, id?: number) => void;
  maxwidth?: string;
  isGeofence?: boolean;
  labelStyle?: CSSProperties;
  iconDirection?: keyof typeof IconButtonDirectionEnum;
  hoverColor?: string;
  // omits the label entirely (icon only) - for icon-only contexts like the
  // Command Bar's Street View/Navigate To/Find Nearby/Maintenance actions,
  // rather than relying on a fixed-size container to visually clip it ...
  hideLabel?: boolean;
}

const getLabelPropValue = (props: TertiaryIconButtonProps, name: string, stackedValue: any) => {
  if (props) {
    if (props[name]) {
      return props[name];
    } else if (!props.iconDirection || props.iconDirection === 'stack') {
      return stackedValue;
    }
  }
  return undefined;
};

const useStyles = makeStyles((theme: Theme) => ({
  root : {
    gap           : '8px',
    color         : dlsColor('darkestBlue'),
    height        : '32px',
    display       : 'flex',
    padding       : '0px 16px',
    fontSize      : '13px',
    minHeight     : '32px',
    fontFamily    : 'Inter',
    fontWeight    : 600,
    lineHeight    : '14px',
    borderRadius  : '8px',
    letterSpacing : '0.5px',
    '&:hover'     : {
      boxShadow       : dlsShadow(3, DLSShadowStyle.DropShadow),
      borderRadius    : '8px !important',
      backgroundColor : ({hoverColor}: TertiaryIconButtonProps) => hoverColor || dlsColor('aquamarine')
    },
    '&:active' : {
      boxShadow       : 'none',
      borderRadius    : '8px',
      backgroundColor : ({hoverColor}: TertiaryIconButtonProps) => hoverColor || dlsColor('aquamarine')
    },
    '&:focus' : {
      outline : 'none'
    },
    '&:focus-visible' : {
      outline       : `2px solid ${dlsColor('skyBlue')}`,
      borderRadius  : '8px',
      outlineOffset : '2px'
    },
    '&$disabled' : {
      color : dlsColor('grey4')
    }
  },
  // the 3px top offset lines a stacked label up under its icon - a label with
  // no icon (e.g. Clear all filters) took it too, and sat 3px low ...
  label : {
    marginRight   : 6,
    marginLeft    : 6,
    marginTop     : (props) => (props.icon ? getLabelPropValue(props, 'margintop', 3) : (props.margintop ?? 0)),
    lineHeight    : (props) => getLabelPropValue(props, 'lineheight', 'normal'),
    maxWidth      : (props: {maxwidth?: string}) => props?.maxwidth,
    textTransform : 'capitalize !important' as any
  }
}));

/**
 * Tertiary buttons can be paired with primary buttons, secondary buttons,
 * or other tertiary buttons with Left, Right & Stack Icons. By Default
 * Icons direction is 'Stack'
 *
 * ```typescript
 *
 * import React from 'react';
 * import {TertiaryIconButton} from '@ion/dls/src/components/buttons/';
 * import {SystemContactIcon} from '@ion/dls/src/icons/';
 *
 * function MyComponent {
 *   return (
 *     <React.Fragment>
 *       <TertiaryIconButton iconDirection='left'
 *         icon={
 *           <SystemContactIcon size={IconSize.medium} />
 *          }
 *          label='Contact' />
 *        <br />
 *       <TertiaryIconButton label='Contact'
 *         icon={
 *           <SystemContactIcon size={IconSize.medium} />
 *          } />
 *     </React.Fragment>
 *   );
 * }
 *
 * ```
 *
 */
export const TertiaryIconButton = (props: TertiaryIconButtonProps) => {
  const {
    id,
    fill,
    icon,
    label,
    onClick,
    maxwidth,
    isGeofence,
    labelStyle,
    hoverColor,
    hideLabel,
    children,
    iconDirection = 'stack',
    ...buttonProps
  } = props;

  const classes = useStyles(props);
  
  const activeColor = useMemo(() => (
    dlsColor(!props.disabled ? 'darkestBlue' : 'grey4')
  ), [props.disabled]);

  const handleClick = useCallback(function(e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
    onClick?.(e, id);
  }, [onClick, id]);

  return (
    <Button disableRipple
      aria-label={hideLabel ? label : undefined}
      onClick={handleClick}
      classes={{
        root : classes.root
      }}
      {...buttonProps}>
      <Grid container
        justifyContent='center'
        alignItems='center'
        style={{
          ...(fill && {
            backgroundColor : dlsColor('white'),
            padding         : '6px 10px',
            borderRadius    : 6
          })
        }}
        direction={
          iconDirection === 'stack'
            ? 'column'
            : iconDirection === 'right'
              ? 'row-reverse'
              : 'row'
        }>
        {icon}
        {!hideLabel && (
          <StableButtonLabel className={classnames(classes.label,
            isGeofence ? OMIT_CLICK_AWAY_LISTENER : '')}
          style={{
            color : activeColor,
            ...labelStyle
          }}>
            {label ?? children}
          </StableButtonLabel>
        )}
      </Grid>
    </Button>
  );
};

export default TertiaryIconButton;
